import { useMemo, useState } from "react";
import type { AuditEntry, AuditActionType } from "@/data/documentTypes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { displayIdentity } from "@/lib/uuid";
import {
  FileText, FileCheck, PenTool, DollarSign, CreditCard, Briefcase,
  ClipboardCheck, AlertTriangle, CheckCircle, Truck, Send, Package,
} from "lucide-react";
import { type UseUserResolverReturn } from "@/hooks/useUserResolver";

const ACTION_ICONS: Record<AuditActionType, React.ElementType> = {
  quote_created: FileText,
  quote_updated: FileText,
  quote_presented: Send,
  quote_won: CheckCircle,
  quote_lost: AlertTriangle,
  contract_generated: FileCheck,
  contract_signed: FileCheck,
  change_order_created: PenTool,
  change_order_signed: PenTool,
  pricing_adjusted: DollarSign,
  credit_issued: CreditCard,
  credit_updated: CreditCard,
  job_created: Briefcase,
  job_stage_changed: Briefcase,
  job_completed: CheckCircle,
  qa_submitted: ClipboardCheck,
  issue_created: AlertTriangle,
  issue_resolved: CheckCircle,
  material_ordered: Package,
  dispatch_assigned: Truck,
  document_sent: Send,
};

const ACTION_COLORS: Record<string, string> = {
  quote_created: "text-info",
  quote_presented: "text-info",
  quote_won: "text-success",
  quote_lost: "text-destructive",
  contract_generated: "text-primary",
  contract_signed: "text-success",
  change_order_created: "text-warning",
  change_order_signed: "text-success",
  credit_issued: "text-destructive",
  issue_created: "text-destructive",
  issue_resolved: "text-success",
  qa_submitted: "text-primary",
  job_completed: "text-success",
};

type FilterGroup = "All" | "Sales" | "Contract" | "Operations" | "Issues";

const FILTER_MAP: Record<FilterGroup, AuditActionType[]> = {
  All: [],
  Sales: ["quote_created", "quote_updated", "quote_presented", "quote_won", "quote_lost", "pricing_adjusted"],
  Contract: ["contract_generated", "contract_signed", "change_order_created", "change_order_signed", "document_sent"],
  Operations: ["job_created", "job_stage_changed", "job_completed", "qa_submitted", "material_ordered", "dispatch_assigned"],
  Issues: ["issue_created", "issue_resolved", "credit_issued", "credit_updated"],
};

interface Props {
  entries: AuditEntry[];
  maxVisible?: number;
  userResolver?: UseUserResolverReturn;
}

export function AuditTrailTimeline({ entries, maxVisible = 15, userResolver }: Props) {
  const [filter, setFilter] = useState<FilterGroup>("All");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    if (filter === "All") return entries;
    const allowedTypes = FILTER_MAP[filter];
    return entries.filter(e => allowedTypes.includes(e.actionType));
  }, [entries, filter]);

  const displayed = showAll ? filtered : filtered.slice(0, maxVisible);

  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground italic text-center py-4">No audit trail entries.</p>;
  }

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex gap-1 flex-wrap">
        {(Object.keys(FILTER_MAP) as FilterGroup[]).map(group => (
          <Button
            key={group}
            variant={filter === group ? "default" : "outline"}
            size="sm"
            className="text-[10px] h-6 px-2"
            onClick={() => { setFilter(group); setShowAll(false); }}
          >
            {group}
          </Button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[13px] top-0 bottom-0 w-px bg-border" />

        <div className="space-y-0">
          {displayed.map((entry, idx) => {
            const Icon = ACTION_ICONS[entry.actionType] || FileText;
            const color = ACTION_COLORS[entry.actionType] || "text-muted-foreground";
            const date = new Date(entry.timestamp);
            const dateStr = date.toLocaleDateString();
            const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            const rowKey = `${entry.id || "audit-entry"}-${entry.timestamp || "no-ts"}-${idx}`;

            return (
              <div key={rowKey} className="flex gap-3 pl-0 py-2 relative">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center border bg-background shrink-0 z-10 ${color}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                   <div className="flex-1 min-w-0 pt-0.5">
                   <p className="text-xs font-medium text-foreground">{entry.description}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
                      <span>{displayIdentity(userResolver?.getName(entry.user) ?? entry.user, "User")}</span>
                      <span>·</span>
                      <span>{dateStr} {timeStr}</span>
                     {entry.previousValue && entry.newValue && (
                       <>
                         <span>·</span>
                         <span className="line-through text-destructive/60">{displayIdentity(entry.previousValue, "[ID]")}</span>
                         <span>→</span>
                         <span className="text-success font-medium">{displayIdentity(entry.newValue, "[ID]")}</span>
                       </>
                     )}
                   </div>
                 </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Show more / less */}
      {filtered.length > maxVisible && (
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "Show Less" : `Show All (${filtered.length})`}
          </Button>
        </div>
      )}

      <div className="text-[10px] text-muted-foreground text-right pt-1 border-t">
        {filtered.length} event{filtered.length !== 1 ? "s" : ""} tracked
      </div>
    </div>
  );
}
